import React, { useEffect, useMemo, useState } from "react"
import Select from "react-select"
import { useMutation, useQuery } from "urql"

import Breadcrumbs from "../../../components/shared/Breadcrumbs"
import { Button } from "../../../components/shared/Buttons"
import { CheckBox, InputWithLabel } from "../../../components/shared/Inputs"
import { Banner } from "../../../components/shared/Layout"
import { ButtonLink } from "../../../components/shared/Links"
import QuillEditor from "../../../components/shared/QuillEditor"
import Typography from "../../../components/shared/Typography"
import { ToastProvider, useToast } from "../../../contexts/ToastContext"
import GraphQLProvider from "../../../providers/GraphQLProvider"
import { selectStyling } from "../../../utils/utils"

import NewsletterEmailPreview from "./NewsletterEmailPreview"

const CLIENTS_QUERY = `
  query NewsletterComposerClients {
    clients {
      id
      firstName
      lastName
      email
    }
  }
`

const AUDIENCE_PREVIEW = `
  query NewsletterAudiencePreview($campaignId: ID!) {
    newsletterAudiencePreview(campaignId: $campaignId) {
      total
      deliverable
      skippedNoEmail
      skippedUnsubscribed
    }
  }
`

const CREATE_CAMPAIGN = `
  mutation CreateNewsletterCampaign($input: NewsletterCampaignInput!) {
    createNewsletterCampaign(input: $input) {
      result
      errors
      campaign { id }
    }
  }
`

const UPDATE_CAMPAIGN = `
  mutation UpdateNewsletterCampaign($campaignId: ID!, $input: NewsletterCampaignInput!) {
    updateNewsletterCampaign(campaignId: $campaignId, input: $input) {
      result
      errors
      campaign { id }
    }
  }
`

const SEND_CAMPAIGN = `
  mutation SendNewsletterCampaign($campaignId: ID!) {
    sendNewsletterCampaign(campaignId: $campaignId) {
      result
      errors
    }
  }
`

const SCHEDULE_CAMPAIGN = `
  mutation ScheduleNewsletterCampaign($campaignId: ID!, $scheduledAt: ISO8601DateTime!) {
    scheduleNewsletterCampaign(campaignId: $campaignId, scheduledAt: $scheduledAt) {
      result
      errors
    }
  }
`

const SEND_TEST = `
  mutation SendNewsletterTestEmail($campaignId: ID!, $mailboxId: ID!, $toEmail: String!) {
    sendNewsletterTestEmail(campaignId: $campaignId, mailboxId: $mailboxId, toEmail: $toEmail) {
      result
      errors
    }
  }
`

function NewsletterComposer({ mailboxes, campaign: initialCampaign, practiceName }) {
  const isEdit = Boolean(initialCampaign?.id)
  const isScheduledEdit = initialCampaign?.status === "scheduled"
  const { showToast } = useToast()
  const [subject, setSubject] = useState(initialCampaign?.subject || "")
  const [htmlBody, setHtmlBody] = useState(initialCampaign?.htmlBody || "")
  const [audienceType, setAudienceType] = useState(initialCampaign?.audienceType || "all_email_clients")
  const [selectedClientIds, setSelectedClientIds] = useState((initialCampaign?.selectedClientIds || []).map(String))
  const defaultSenderId = () => {
    const preferred = mailboxes.find((m) => m.isPreferred && m.sendable !== false)
    const firstSendable = mailboxes.find((m) => m.status === "active" && m.sendable !== false)
    const fallback = preferred || firstSendable || mailboxes.find((m) => m.status === "active") || mailboxes[0]
    return fallback?.id != null ? String(fallback.id) : ""
  }
  const [senderMailboxId, setSenderMailboxId] = useState(
    initialCampaign?.senderMailboxId != null ? String(initialCampaign.senderMailboxId) : defaultSenderId()
  )
  const [fallbackMailboxId, setFallbackMailboxId] = useState(
    initialCampaign?.fallbackMailboxId != null ? String(initialCampaign.fallbackMailboxId) : ""
  )
  const [autoFailoverEnabled, setAutoFailoverEnabled] = useState(initialCampaign?.autoFailoverEnabled ?? true)
  const [attestationAccepted, setAttestationAccepted] = useState(initialCampaign?.attestationAccepted || false)
  const [testEmail, setTestEmail] = useState("")
  const [scheduledAt, setScheduledAt] = useState(() => {
    if (!initialCampaign?.scheduledAt) return ""
    const date = new Date(initialCampaign.scheduledAt)
    if (Number.isNaN(date.getTime())) return ""
    const pad = (n) => String(n).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
      date.getMinutes()
    )}`
  })
  const [campaignId, setCampaignId] = useState(initialCampaign?.id || null)
  const [saving, setSaving] = useState(false)

  const [{ data: clientsData }] = useQuery({ query: CLIENTS_QUERY })
  const [{ data: previewData }, reexecutePreview] = useQuery({
    query: AUDIENCE_PREVIEW,
    variables: { campaignId },
    pause: !campaignId
  })

  const [, createCampaign] = useMutation(CREATE_CAMPAIGN)
  const [, updateCampaign] = useMutation(UPDATE_CAMPAIGN)
  const [, sendCampaign] = useMutation(SEND_CAMPAIGN)
  const [, scheduleCampaign] = useMutation(SCHEDULE_CAMPAIGN)
  const [, sendTestEmail] = useMutation(SEND_TEST)

  const activeMailboxes = useMemo(() => mailboxes.filter((m) => m.status === "active"), [mailboxes])
  const sendableMailboxes = useMemo(() => activeMailboxes.filter((m) => m.sendable !== false), [activeMailboxes])
  const fallbackOptions = useMemo(
    () => sendableMailboxes.filter((m) => String(m.id) !== String(senderMailboxId)),
    [sendableMailboxes, senderMailboxId]
  )
  const canUseFailover = fallbackOptions.length > 0

  const clientOptions = useMemo(
    () =>
      (clientsData?.clients || [])
        .filter((c) => c.email)
        .map((c) => ({
          value: c.id,
          label: `${c.firstName}${c.lastName ? ` ${c.lastName}` : ""} (${c.email})`
        })),
    [clientsData]
  )

  const buildInput = () => ({
    subject,
    htmlBody,
    audienceType,
    selectedClientIds: audienceType === "selected_clients" ? selectedClientIds : [],
    senderMailboxId: senderMailboxId || null,
    fallbackMailboxId: canUseFailover && autoFailoverEnabled ? fallbackMailboxId || null : null,
    autoFailoverEnabled: canUseFailover && autoFailoverEnabled,
    attestationAccepted
  })

  const saveDraft = async () => {
    setSaving(true)
    try {
      if (campaignId) {
        const result = await updateCampaign({ campaignId, input: buildInput() })
        if (result.data?.updateNewsletterCampaign?.result !== "success") {
          throw new Error(result.data?.updateNewsletterCampaign?.errors?.[0] || "Update failed")
        }
        showToast("Draft saved")
        reexecutePreview({ requestPolicy: "network-only" })
        return campaignId
      }

      const result = await createCampaign({ input: buildInput() })
      if (result.data?.createNewsletterCampaign?.result !== "success") {
        throw new Error(result.data?.createNewsletterCampaign?.errors?.[0] || "Create failed")
      }
      const newId = result.data.createNewsletterCampaign.campaign.id
      setCampaignId(newId)
      window.history.replaceState({}, "", `/portal/settings/newsletter/campaigns/${newId}/edit`)
      showToast("Draft saved")
      reexecutePreview({ requestPolicy: "network-only" })
      return newId
    } catch (error) {
      showToast({ type: "error", content: error instanceof Error ? error.message : "Save failed" })
      return null
    } finally {
      setSaving(false)
    }
  }

  const ensureSaved = async () => {
    if (campaignId) return campaignId
    return saveDraft()
  }

  const handleSendNow = async () => {
    try {
      const id = await ensureSaved()
      if (!id) return
      const result = await sendCampaign({ campaignId: id })
      if (result.data?.sendNewsletterCampaign?.result === "success") {
        showToast("Campaign sending started")
        window.location.href = `/portal/settings/newsletter/campaigns/${id}`
      } else {
        throw new Error(result.data?.sendNewsletterCampaign?.errors?.[0] || "Send failed")
      }
    } catch (error) {
      showToast({ type: "error", content: error instanceof Error ? error.message : "Send failed" })
    }
  }

  const handleSchedule = async () => {
    if (!scheduledAt) {
      showToast({ type: "error", content: "Choose a schedule date and time" })
      return
    }
    try {
      const id = await ensureSaved()
      if (!id) return
      const result = await scheduleCampaign({
        campaignId: id,
        scheduledAt: new Date(scheduledAt).toISOString()
      })
      if (result.data?.scheduleNewsletterCampaign?.result === "success") {
        showToast("Campaign scheduled")
        window.location.href = `/portal/settings/newsletter/campaigns/${id}`
      } else {
        throw new Error(result.data?.scheduleNewsletterCampaign?.errors?.[0] || "Schedule failed")
      }
    } catch (error) {
      showToast({ type: "error", content: error instanceof Error ? error.message : "Schedule failed" })
    }
  }

  const handleTestSend = async () => {
    if (!testEmail) {
      showToast({ type: "error", content: "Enter a test email address" })
      return
    }
    if (!senderMailboxId) {
      showToast({ type: "error", content: "Select a sender mailbox" })
      return
    }
    const selectedMailbox = sendableMailboxes.find((m) => String(m.id) === String(senderMailboxId))
    if (!selectedMailbox) {
      showToast({
        type: "error",
        content: "Selected mailbox cannot send. Reconnect it from Newsletter settings."
      })
      return
    }
    try {
      const id = await ensureSaved()
      if (!id) return
      const result = await sendTestEmail({
        campaignId: id,
        mailboxId: selectedMailbox.id,
        toEmail: testEmail
      })
      if (result.data?.sendNewsletterTestEmail?.result === "success") {
        showToast("Test email sent")
      } else {
        throw new Error(result.data?.sendNewsletterTestEmail?.errors?.[0] || "Test send failed")
      }
    } catch (error) {
      showToast({ type: "error", content: error instanceof Error ? error.message : "Test send failed" })
    }
  }

  useEffect(() => {
    if (campaignId) {
      reexecutePreview({ requestPolicy: "network-only" })
    }
  }, [campaignId, audienceType, selectedClientIds])

  useEffect(() => {
    if (!canUseFailover) {
      setFallbackMailboxId("")
      return
    }
    if (fallbackMailboxId && !fallbackOptions.some((m) => String(m.id) === String(fallbackMailboxId))) {
      setFallbackMailboxId("")
    }
  }, [canUseFailover, fallbackMailboxId, fallbackOptions])

  const preview = previewData?.newsletterAudiencePreview
  const canSend = sendableMailboxes.length > 0 && attestationAccepted && (preview?.deliverable || 0) > 0
  const selectedSender = sendableMailboxes.find((m) => String(m.id) === String(senderMailboxId))

  return (
    <div>
      <Breadcrumbs
        links={[
          { href: "/portal/settings", text: "Settings" },
          { href: "/portal/settings/newsletter/campaigns", text: "Campaigns" }
        ]}
        activePage={isEdit ? (isScheduledEdit ? "Edit scheduled campaign" : "Edit campaign") : "New campaign"}
      />
      <Typography as="h1" variant="h3" className="mb-6">
        {isEdit ? (isScheduledEdit ? "Edit scheduled campaign" : "Edit campaign") : "New campaign"}
      </Typography>

      {isScheduledEdit && (
        <Banner type="info" className="mb-6">
          This campaign is scheduled. You can update the content and audience until the send time.
        </Banner>
      )}

      {sendableMailboxes.length === 0 && (
        <Banner type="error" className="mb-6">
          Connect an active Gmail or Outlook mailbox before sending.
          <ButtonLink href="/portal/settings/newsletter" className="ml-2">
            Connect mailbox
          </ButtonLink>
        </Banner>
      )}
      {activeMailboxes.length > 0 && sendableMailboxes.length === 0 && (
        <Banner type="error" className="mb-6">
          Your connected mailbox needs to be reconnected before you can send.
          <ButtonLink href="/portal/settings/newsletter" className="ml-2">
            Reconnect mailbox
          </ButtonLink>
        </Banner>
      )}

      <div className="grid grid-cols-2 items-start gap-8 lg:grid-cols-1">
        <div className="grid grid-cols-1 gap-8">
          <InputWithLabel label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />

          <div>
            <Typography variant="body" className="mb-2 font-semibold">
              Email body
            </Typography>
            <QuillEditor value={htmlBody} onChange={setHtmlBody} />
            <Typography variant="body" className="mt-2 text-gray-dark">
              Use {"{{client_first_name}}"}, {"{{client_name}}"}, or {"{{practice_name}}"} for personalization.
            </Typography>
          </div>

          <div>
            <Typography variant="body" className="mb-2 font-semibold">
              Audience
            </Typography>
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={audienceType === "all_email_clients"}
                  onChange={() => setAudienceType("all_email_clients")}
                />
                All clients with email
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={audienceType === "selected_clients"}
                  onChange={() => setAudienceType("selected_clients")}
                />
                Selected clients
              </label>
            </div>
            {audienceType === "selected_clients" && (
              <Select
                isMulti
                className="mt-4"
                styles={selectStyling()}
                options={clientOptions}
                value={clientOptions.filter((o) => selectedClientIds.includes(o.value))}
                onChange={(selected) => setSelectedClientIds((selected || []).map((s) => s.value))}
                placeholder="Select clients"
              />
            )}
            {preview && (
              <Typography variant="body" className="mt-3 text-gray-dark">
                Audience preview: {preview.deliverable} deliverable / {preview.total} total (
                {preview.skippedUnsubscribed} unsubscribed, {preview.skippedNoEmail} without email)
              </Typography>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-1">
            <div>
              <label className="mb-2 block text-sm font-semibold">Sender mailbox</label>
              <select
                className="w-full rounded-md border border-gray-light px-3 py-2"
                value={senderMailboxId}
                onChange={(e) => setSenderMailboxId(e.target.value)}>
                {sendableMailboxes.map((mailbox) => (
                  <option key={mailbox.id} value={mailbox.id}>
                    {mailbox.provider}: {mailbox.emailAddress}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold">Fallback mailbox</label>
              <select
                className="w-full rounded-md border border-gray-light px-3 py-2"
                value={fallbackMailboxId}
                disabled={!canUseFailover}
                onChange={(e) => setFallbackMailboxId(e.target.value)}>
                <option value="">None</option>
                {fallbackOptions.map((mailbox) => (
                  <option key={mailbox.id} value={mailbox.id}>
                    {mailbox.provider}: {mailbox.emailAddress}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <CheckBox
            label="Automatically retry on fallback mailbox if sender fails"
            checked={autoFailoverEnabled}
            disabled={!canUseFailover}
            onChange={(e) => setAutoFailoverEnabled(e.target.checked)}
          />
          {!canUseFailover && (
            <Typography variant="body" className="-mt-4 text-gray-dark">
              Connect a second mailbox to enable automatic failover.
            </Typography>
          )}

          <CheckBox
            label="I have permission to email these clients"
            checked={attestationAccepted}
            onChange={(e) => setAttestationAccepted(e.target.checked)}
          />

          <div className="rounded-xl border border-gray-light p-4">
            <Typography variant="body" className="mb-3 font-semibold">
              Test send
            </Typography>
            <div className="flex gap-3 sm:flex-col">
              <InputWithLabel
                label="Test email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="grow"
              />
              <Button type="secondary" className="self-end" onClick={handleTestSend}>
                Send test
              </Button>
            </div>
          </div>

          <div className="rounded-xl border border-gray-light p-4">
            <Typography variant="body" className="mb-3 font-semibold">
              Schedule
            </Typography>
            <div className="flex gap-3 sm:flex-col">
              <InputWithLabel
                label="Send at"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
              <Button type="secondary" className="self-end" onClick={handleSchedule} disabled={!canSend}>
                Schedule send
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button type="tertiary" onClick={saveDraft} disabled={saving}>
              Save draft
            </Button>
            <Button type="primary" onClick={handleSendNow} disabled={!canSend}>
              Send now
            </Button>
            <ButtonLink href="/portal/settings/newsletter/campaigns" variant="secondary">
              Cancel
            </ButtonLink>
          </div>
        </div>

        <div className="sticky top-4 lg:static">
          <NewsletterEmailPreview
            subject={subject}
            htmlBody={htmlBody}
            practiceName={practiceName}
            fromEmail={selectedSender?.emailAddress}
          />
        </div>
      </div>
    </div>
  )
}

export default function NewsletterComposerPage(props) {
  return (
    <GraphQLProvider>
      <ToastProvider>
        <NewsletterComposer {...props} />
      </ToastProvider>
    </GraphQLProvider>
  )
}
